import HireOptions from 'components/pages/HireFoodTruckPage/HireOptions'
import PageMeta from 'components/PageMeta'
import React, { useEffect } from 'react'
import PageLayout from 'components/pages/PageLayout'
import Title from 'components/ui/Title'
import { withApollo } from 'components/withApollo'
import { GetStaticPathsResult, NextPage } from 'next'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import ChatWidget from 'components/ChatWidget'
import { useRouter } from 'next/router'
import { useCateringRequestPreferredTruck } from 'components/pages/CateringRequestFormPage/types/CateringRequestPreferredTruck'
import { useQuery } from '@apollo/client'
import { InformationCircleIcon } from '@heroicons/react/outline'
import cn from 'classnames'

import {
  CateringFormOrganization,
  CateringFormOrganizationVariables
} from '../CateringRequestFormPage/types/CateringFormOrganization'
import { ORGANIZATION_QUERY } from '../CateringRequestFormPage'

type Props = {}

const HireFoodTruckPage = () => {
  const router = useRouter()
  const {
    truckId: preferredTruckId,
    organizer: organizerSeoName,
    note: urlNote
  } = (router.query || {}) as {
    truckId?: string
    organizer?: string
    note?: string
  }

  const { data: truckData } = useCateringRequestPreferredTruck({
    variables: {
      id: preferredTruckId ? parseInt(preferredTruckId, 10) : null
    },
    skip: !preferredTruckId,
    fetchPolicy: 'cache-first'
  })

  const { data: organizationData } = useQuery<
    CateringFormOrganization,
    CateringFormOrganizationVariables
  >(ORGANIZATION_QUERY, {
    variables: {
      name: organizerSeoName!
    },
    fetchPolicy: 'cache-first',
    skip: !organizerSeoName || !organizerSeoName.length
  })

  useEffect(() => {
    if (urlNote) {
      sessionStorage.setItem('cateringNote', urlNote)
    }
  }, [urlNote])

  return (
    <div>
      <ChatWidget />
      <PageMeta
        canonicalPath="/hire-food-truck"
        title={`Best Food Trucks | Hire a food truck${
          organizationData ? ` from ${organizationData.organization?.name}` : ''
        }`}
      />
      <PageLayout hideNavigation={!!organizationData} gray staticPage>
        <div className="min-h-screen">
          {organizationData && organizationData.organization?.cateringLogo && (
            <div className="flex items-center justify-center mt-10">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt={`${organizationData.organization.name} logo`}
                className="bg-center object-cover"
                src={organizationData.organization?.cateringLogo.variantUrl}
              />
            </div>
          )}
          <div
            className={cn({
              'lg:mt-10': !organizationData?.organization?.cateringLogo
            })}
          >
            <Title
              className="lg:pt-14 mb-4 uppercase font-blockBertholdBQ text-4xl lg:text-6xl tracking-wide text-orange-500"
              variant="pageTitle"
            >
              Hire a food truck
            </Title>
            {truckData?.truck && (
              <div className="text-center mb-8 border rounded-full border-orange-200 bg-orange-50 py-2 px-4 mx-auto max-w-max">
                <span className="font-light text-center mb-8">
                  My preferred truck is{' '}
                </span>
                <span className="text-xl font-bold font-blockBertholdBQ text-orange-600">
                  {truckData.truck.name}
                </span>
              </div>
            )}
          </div>
          <HireOptions organizerSeoName={organizerSeoName} />
          {organizationData && (
            <div className="flex flex-row my-10 max-w-xl mx-auto">
              <div>
                <InformationCircleIcon className="text-gray-800 w-6 h-6" />
              </div>

              <p className="text-justify text-gray-900 font-light px-2">
                This catering is organized by{' '}
                <span className="font-bold">
                  {organizationData.organization?.name}
                </span>
                . If you&apos;d like to discuss your needs with one of our
                booking specialists directly, you can send an email to:{' '}
                <a
                  className="underline font-bold"
                  href={`mailto:${
                    organizationData.organization?.cateringEmailCustomer ||
                    'sales@bestfoodtrucks.com'
                  }`}
                >
                  {organizationData.organization?.cateringEmailCustomer ||
                    'sales@bestfoodtrucks.com'}
                </a>
              </p>
            </div>
          )}
        </div>
      </PageLayout>
    </div>
  )
}

export default withApollo({ ssr: false })(HireFoodTruckPage as NextPage)

export const getStaticProps = getStaticApolloProps<Props>(HireFoodTruckPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking'
  }
}
